import {
  BuildingOffice2Icon,
  ChartBarIcon,
  ChatBubbleBottomCenterTextIcon,
  CheckCircleIcon,
  CurrencyEuroIcon,
  DocumentDuplicateIcon,
  EnvelopeIcon,
  HomeModernIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';

import { showToast } from '../../../../common/Notification';
import Popup from '../../../../common/Popup/Popup.module';
import {
  FIRST_CONTACT_TEMPLATE,
  FIRST_CONTACT_TEMPLATE_DESCRIPTIONS,
  FIRST_CONTACT_TEMPLATE_LABELS,
  FIRST_CONTACT_TEMPLATE_OPTIONS,
} from '../../utils/emailContentGenerator';

const DEFAULT_TEMPLATE_ICONS: Record<string, React.ElementType> = {
  [FIRST_CONTACT_TEMPLATE.REFERENT_ONLY]: ChatBubbleBottomCenterTextIcon,
  [FIRST_CONTACT_TEMPLATE.DPE_REPORT]: ChartBarIcon,
  [FIRST_CONTACT_TEMPLATE.COPRO_REPORT]: BuildingOffice2Icon,
  [FIRST_CONTACT_TEMPLATE.BOTH_REPORTS]: DocumentDuplicateIcon,
  [FIRST_CONTACT_TEMPLATE.TRANSACTION_V1]: HomeModernIcon,
  [FIRST_CONTACT_TEMPLATE.TRANSACTION_V2]: CurrencyEuroIcon,
};

const DEFAULT_TEMPLATE_OPTIONS: string[] = [...FIRST_CONTACT_TEMPLATE_OPTIONS];

interface EmailPreviewProps {
  isOpen: boolean;
  onClose: () => void;
  emailContent: string;
  emailSubject: string;
  emailTo: string;
  onCopy?: () => Promise<void> | void;
  onOpenMailApp?: () => Promise<void> | void;
  onSendFromReferent?: () => Promise<void> | void;
  viewOnly?: boolean;
  templateType?: string;
  onTemplateChange?: (templateType: string) => void;
  templateLabels?: Record<string, string>;
  templateDescriptions?: Record<string, string>;
  templateOptions?: string[];
  templateIcons?: Record<string, React.ElementType>;
  actionRegisteredMessage?: string;
  noCrmTracking?: boolean;
  /** Optional: template marked as the company-wide default (shows a "Modèle par défaut" badge). */
  defaultTemplateType?: string;
  /** Optional: callback invoked when the user clicks the "Modifier ce défaut" link. */
  onEditDefaultTemplate?: () => void;
}

export function EmailPreview({
  isOpen,
  onClose,
  emailContent,
  emailSubject,
  emailTo,
  onCopy,
  onOpenMailApp,
  onSendFromReferent,
  viewOnly = false,
  templateType = FIRST_CONTACT_TEMPLATE.REFERENT_ONLY,
  onTemplateChange,
  templateLabels = FIRST_CONTACT_TEMPLATE_LABELS,
  templateDescriptions = FIRST_CONTACT_TEMPLATE_DESCRIPTIONS,
  templateOptions = DEFAULT_TEMPLATE_OPTIONS,
  templateIcons = DEFAULT_TEMPLATE_ICONS,
  actionRegisteredMessage,
  noCrmTracking = false,
  defaultTemplateType,
  onEditDefaultTemplate,
}: EmailPreviewProps) {
  const [isCopied, setIsCopied] = useState(false);
  const [actionRegistered, setActionRegistered] = useState(false);
  const [isSendingFromReferent, setIsSendingFromReferent] = useState(false);
  const canSendFromReferent = Boolean(onSendFromReferent);

  useEffect(() => {
    if (isOpen) {
      setIsCopied(false);
      setActionRegistered(false);
      setIsSendingFromReferent(false);
    }
  }, [isOpen]);

  const formatEmailContent = (content: string) => {
    return content.split('\n').map((line, index, array) => (
      `${line}${index < array.length - 1 ? '<br />' : ''}`
    )).join('');
  };

  const handleCopyContent = () => {
    const contentToCopy = emailSubject
      ? `Objet : ${emailSubject}\n\n${emailContent}`
      : emailContent;

    navigator.clipboard.writeText(contentToCopy)
      .then(() => {
        setIsCopied(true);
        showToast('success', 'Contenu copié avec succès !');
        setTimeout(() => setIsCopied(false), 2000);

        if (!actionRegistered && onCopy) {
          onCopy();
          setActionRegistered(true);
        }
      })
      .catch((error) => {
        console.error('Erreur lors de la copie :', error);
        showToast('error', 'Erreur lors de la copie du contenu.');
      });
  };

  const handleOpenMailApp = () => {
    const mailtoLink = `mailto:${emailTo}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailContent)}`;
    window.open(mailtoLink, '_blank');

    if (!actionRegistered && onOpenMailApp) {
      onOpenMailApp();
      setActionRegistered(true);
    }
  };

  const handleSendFromReferent = async() => {
    if (!onSendFromReferent) {
      return;
    }

    try {
      setIsSendingFromReferent(true);
      await onSendFromReferent();
      if (!actionRegistered && onCopy) {
        await onCopy();
      }
      setActionRegistered(true);
    } catch (error) {
      console.error('Erreur lors de l\'envoi SMTP référent :', error);
    } finally {
      setIsSendingFromReferent(false);
    }
  };

  const renderTemplateSelector = () => {
    if (viewOnly || !onTemplateChange) {return null;}

    return (
      <div className="mb-5">
        <div className="mb-2.5 flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-700">Choisir le modèle d'email</span>
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
            {templateLabels[templateType] ?? templateType}
          </span>
          {defaultTemplateType && onEditDefaultTemplate && (
            <button
              type="button"
              onClick={onEditDefaultTemplate}
              className="ml-auto text-xs font-medium text-rgpd-blue hover:underline"
            >
              Modifier le modèle par défaut →
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {templateOptions.map((option) => {
            const isSelected = templateType === option;
            const isDefault = defaultTemplateType === option;
            const Icon = templateIcons[option] ?? ChatBubbleBottomCenterTextIcon;
            return (
              <button
                key={option}
                type="button"
                onClick={() => onTemplateChange(option)}
                className={`group relative flex flex-col items-center rounded-lg border-2 px-3 py-3 text-center transition-all ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50 shadow-sm'
                    : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/50'
                }`}
              >
                {isSelected && (
                  <span className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-blue-500">
                    <CheckCircleIcon className="h-4 w-4 text-white" strokeWidth={2.5} />
                  </span>
                )}
                {isDefault && (
                  <span className="absolute -top-2 left-2 inline-flex items-center gap-0.5 rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 shadow-sm">
                    <span role="img" aria-label="Étoile">★</span>
                    <span>Modèle par défaut</span>
                  </span>
                )}
                <Icon className={`mb-1.5 h-6 w-6 ${isSelected ? 'text-blue-600' : 'text-gray-400 group-hover:text-blue-400'}`} />
                <span className={`text-sm leading-tight font-medium ${isSelected ? 'text-blue-700' : 'text-gray-700'}`}>
                  {templateLabels[option] ?? option}
                </span>
                <span className={`mt-1 text-xs leading-snug ${isSelected ? 'text-blue-600/80' : 'text-gray-400'}`}>
                  {templateDescriptions[option] ?? ''}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderSendActions = () => {
    if (viewOnly) {return null;}

    return (
      <div className="mb-5">
        <div className="mb-2.5 flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-700">Envoyer l'email</span>
        </div>

        {(actionRegistered || noCrmTracking) && actionRegisteredMessage && (
          <div className="mb-3 flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2.5">
            <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
            <p className="text-sm text-green-700">
              {actionRegisteredMessage}
            </p>
          </div>
        )}

        {actionRegistered && !noCrmTracking && !actionRegisteredMessage && (
          <div className="mb-3 flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2.5">
            <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
            <p className="text-sm text-green-700">
              Action enregistrée. Le propriétaire passera à l'étape suivante du suivi. Vous pouvez fermer cette fenêtre.
            </p>
          </div>
        )}

        <div className="flex flex-wrap items-stretch gap-2">
          {canSendFromReferent && (
            <button
              onClick={handleSendFromReferent}
              disabled={isSendingFromReferent}
              className="flex grow items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <PaperAirplaneIcon className="h-4 w-4" />
              {isSendingFromReferent ? 'Envoi en cours...' : 'Envoyer depuis le mail référent'}
            </button>
          )}
          <button
            onClick={handleCopyContent}
            className={`flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors ${
              isCopied
                ? 'border-green-300 bg-green-50 text-green-700'
                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
            }`}
          >
            <DocumentDuplicateIcon className="h-4 w-4" />
            {isCopied ? 'Copié !' : 'Copier'}
          </button>
          <button
            onClick={handleOpenMailApp}
            className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-50"
          >
            <EnvelopeIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Ouvrir dans Mail</span>
            <span className="sm:hidden">Mail</span>
          </button>
        </div>

        {!actionRegistered && !noCrmTracking && (
          <p className="mt-2 text-xs text-amber-600">
            Toute action ci-dessus enregistrera l'envoi dans le CRM.
          </p>
        )}
      </div>
    );
  };

  return (
    <Popup
      isOpen={isOpen}
      onClose={onClose}
      title={viewOnly ? 'Contenu du mail envoyé' : "Aperçu de l'email"}
      width="80%"
      maxWidth="800px"
      height="auto"
      maxHeight="85vh"
      additionalClass="email-preview-popup"
    >
      <div className="flex h-full flex-col gap-0">
        {renderTemplateSelector()}

        {renderSendActions()}

        {viewOnly && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5">
            <EnvelopeIcon className="mt-0.5 h-5 w-5 shrink-0 text-blue-500" />
            <p className="text-sm text-blue-700">
              Ceci est le contenu du mail qui a été envoyé au propriétaire.
            </p>
          </div>
        )}

        <div className="min-h-0 grow overflow-auto rounded-lg border border-gray-200 bg-white">
          <div className="sticky top-0 z-10 border-b border-gray-200 bg-gray-50 px-4 py-2.5">
            <p className="text-sm text-gray-600"><span className="font-medium text-gray-700">À :</span> {emailTo}</p>
            <p className="text-sm text-gray-600"><span className="font-medium text-gray-700">Objet :</span> {emailSubject}</p>
          </div>
          <div
            className="px-4 py-3 text-sm leading-relaxed whitespace-pre-line text-gray-800"
            dangerouslySetInnerHTML={{ __html: formatEmailContent(emailContent) }}
          />
        </div>
      </div>
    </Popup>
  );
}

export default EmailPreview;
